import { getExtensions } from '@/components/tiptap-editor/tiptap-editor';
import {
    attrsToBuilderProps,
    getShortAnswerToolLabelByProperties,
    normalizeAssessmentOptionStrings,
} from '@/components/tiptap-editor/tiptap-extension/short-answer-extension/tool-settings';
import {
    containsZyConsole,
    migrateZyConsoleFormat,
} from '@/components/tiptap-editor/tiptap-extension/zyconsole-extension/zyconsole-migration';
import { migrateZyFctNameHtml } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyfctname-inline-extension';
import { normalizeVersion } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-extension';
import { generateJSON, type JSONContent } from '@tiptap/react';
import { v4 as uuidv4 } from 'uuid';
import { renderMarkdownToHtml } from '../prosemirror-markdown';
import { zyAuthor } from '../servers';
import {
    cleanInlineLatex,
    cleanMalformedBlockMath,
    detectLanguageFromCodeSnippet,
    escapeBareAmpersandsInXmlAttributeValues,
    escapeXml,
    formatIndentedTextForHtml,
    generateImgTag,
    getXmlFromTag,
    isMissingZytagSnippetError,
    markZydefnBlocksInHtml,
    removeAllHtmlNamedEntities,
    unescapeXml,
} from '../utils';
import { zyxmlToJson, type ZyXmlToJsonResponse } from '../zybooks-api';
import { buildZyITLabHtml } from './node-parser/zyitlab-import';

const SHORT_ANSWER_IMPORT_OPTION_SKIP = new Set([
    'text',
    'answers',
    'answer',
    'preferred',
    'explanation',
    'hint',
    'solution',
    'render_as_html',
    'render_html',
    'rows',
    'columns',
    'lines',
    'obey_case',
    'obey_space',
    'obey_quote',
    'obey_linear',
    'text_before',
    'text_after',
    'ignoreend',
    'assessment_type',
    'notation',
    'type',
    'strip_ending_newlines',
]);

function shortAnswerQuestionExtraAssessmentOptions(question: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(question)) {
        if (SHORT_ANSWER_IMPORT_OPTION_SKIP.has(k)) {
            continue;
        }
        if (v === undefined || v === null) {
            continue;
        }
        out[k] = v;
    }
    return normalizeAssessmentOptionStrings(out);
}

export interface Section {
    activities: any[];
    chapter_number: string;
    content_resources: ContentResource[];
    full_title: string;
    gdoc_id: string | null;
    id: string;
    parent_gdoc_id: string | null;
    section_number: number;
    short_title: string;
    title: string;
    [key: string]: any;
}

export interface ContentResource {
    type: string;
    payload: Record<string, any>;
    parts: number;
    gdoc_id: string | null;
    parent_gdoc_id: string | null;
    index_items: any[];
    activity_type: string | null;
    optional: boolean | null;
    guid: string;
    sectionMetadata?: Record<string, any>;
    footnoteReferences?: Map<string, string>;
    [key: string]: any;
}

export interface zybookHTMLContent {
    text: string;
    href?: string;
    guid?: string;
    reference?: string;
    attributes?: Array<{
        type: string;
        [key: string]: any;
    }>;

    // zyInlineEnum attribute
    enumid?: string;
}

interface ZyIframeHtmlContent extends zybookHTMLContent {
    src?: string;
    title?: string;
    width?: string;
    height?: string;
    sandbox?: string;
    wiley_hosted?: boolean;
}

export interface ZyVersionFromXml {
    version: string;
    rawTagAndContent: string;
    rawContent: string;
    /** All descendant elements under the zyVersion content that have an `id` attribute, in document order. */
    elementsWithId: Array<{ elementName: string; id: string }>;
    /**
     * Present only when elementsWithId is empty (no id in the zyVersion subtree). Contains the
     * nearest ancestor element that carries an id attribute, walking up from the zyVersion
     * toward the document root.
     *
     * We don't use this for now because we replace inline zyVersion using regex.
     * But we keep it here if we need to use it in the future.
     */
    ancestorWithId?: { elementName: string; id: string };
}

export interface ZyVersionIdEntry {
    /** Whether the id was found on a descendant element inside the zyVersion, or on its nearest ancestor. */
    type: 'directChild' | 'ancestor';
    /** Index of the zyVersion in the source array (from getZyVersionsFromRawZyXml). */
    index: number;
    /** The version string from the zyVersion tag, ready to use as a wrapper attribute. */
    version: string;
}

const ZY_VERSION_OPEN_PREFIX = '<zyVersion';

const ZY_VERSION_CLOSE = '</zyVersion>';

/**
 * Extracts the node ID from a GUID.
 * @param guid - The GUID to extract the node ID from.
 * @returns The node ID.
 */
function getNodeIdFromGuid(guid: string): string {
    const [_, nodeId] = guid.split('_'); // eslint-disable-line @typescript-eslint/no-unused-vars
    return nodeId;
}

/**
 * Removes XML comments from ZyXML (including multiline).
 * ZyAuthor drops these when building content-resource HTML; import must too.
 */
function removeZyComments(rawZyXml: string): string {
    return rawZyXml.replace(/<!--[\s\S]*?-->/g, '');
}

/**
 * Normalize zyCode body text extracted from raw ZyXML for TipTap HTML round-trip:
 * strip XML comments (legacy/disabled lines) and decode entities so `escapeXml` + HTML parse
 * yields real characters (`<`) instead of leaving `&lt;` / `&amp;lt;` in the editor.
 */
function normalizeZyCodeInnerText(content: string): string {
    return unescapeXml(removeZyComments(content));
}

function isZyVersionOpenTagAt(xml: string, idx: number): boolean {
    if (idx < 0 || idx + ZY_VERSION_OPEN_PREFIX.length > xml.length) return false;
    if (xml.slice(idx, idx + ZY_VERSION_OPEN_PREFIX.length) !== ZY_VERSION_OPEN_PREFIX) return false;
    const after = xml[idx + ZY_VERSION_OPEN_PREFIX.length];
    return after === undefined || /[\s/>]/.test(after);
}

/** Finds the closing `</zyVersion>` that matches the open whose inner markup begins at `contentStart`. */
function findBalancedZyVersionClose(xml: string, contentStart: number): { closeTagStart: number; afterClose: number } | null {
    let depth = 1;
    let pos = contentStart;
    const closeLen = ZY_VERSION_CLOSE.length;

    while (depth > 0 && pos < xml.length) {
        let nextOpen = -1;
        let scan = pos;
        while (scan < xml.length) {
            const i = xml.indexOf(ZY_VERSION_OPEN_PREFIX, scan);
            if (i === -1) break;
            if (isZyVersionOpenTagAt(xml, i)) {
                nextOpen = i;
                break;
            }
            scan = i + 1;
        }

        const nextClose = xml.indexOf(ZY_VERSION_CLOSE, pos);
        if (nextClose === -1) return null;

        if (nextOpen !== -1 && nextOpen < nextClose) {
            depth++;
            const gt = xml.indexOf('>', nextOpen);
            if (gt === -1) return null;
            pos = gt + 1;
        } else {
            depth--;
            if (depth === 0) {
                return { closeTagStart: nextClose, afterClose: nextClose + closeLen };
            }
            pos = nextClose + closeLen;
        }
    }

    return null;
}

/**
 * Lists every `<zyVersion>...</zyVersion>` span in document order using balanced matching.
 * Non‑greedy regex cannot do this when outer blocks contain nested `<zyVersion>` children.
 */
function extractZyVersionSpans(xml: string): Array<{ rawTagAndContent: string; attrsString: string; rawContent: string }> {
    const spans: Array<{ rawTagAndContent: string; attrsString: string; rawContent: string }> = [];
    let searchPos = 0;

    while (searchPos < xml.length) {
        const openIdx = xml.indexOf(ZY_VERSION_OPEN_PREFIX, searchPos);
        if (openIdx === -1) break;
        if (!isZyVersionOpenTagAt(xml, openIdx)) {
            searchPos = openIdx + 1;
            continue;
        }

        const gtIdx = xml.indexOf('>', openIdx);
        if (gtIdx === -1) break;

        const attrsString = xml.slice(openIdx + ZY_VERSION_OPEN_PREFIX.length, gtIdx).trim();
        const contentStart = gtIdx + 1;
        const closeInfo = findBalancedZyVersionClose(xml, contentStart);
        if (!closeInfo) {
            searchPos = openIdx + 1;
            continue;
        }

        const { closeTagStart, afterClose } = closeInfo;
        const rawContent = xml.slice(contentStart, closeTagStart);
        const rawTagAndContent = xml.slice(openIdx, afterClose);
        spans.push({ rawTagAndContent, attrsString, rawContent });
        searchPos = openIdx + 1;
    }

    return spans;
}

/**
 * Clears inner content of configured elements (e.g. zyTool, zyAnimator); opening and closing tags remain.
 * @param rawZyXml - The raw ZyXML string.
 * @param tags - The tags to remove content from.
 * @returns
 */
function removeTagContent(rawZyXml: string, tags: string[]): string {
    let out = rawZyXml;
    for (const tag of tags) {
        const re = new RegExp(`(<${tag}(?:\\s[^>]*)?>)([\\s\\S]*?)(<\\/${tag}>)`, 'gi');
        out = out.replace(re, '$1$3');
    }
    return out;
}

/**
 * Parses raw zyXML and returns structured data for every <zyVersion> block in document order.
 *
 * For each block:
 * - version: value of the `version` attribute on the zyVersion tag.
 * - rawTagAndContent: the full "<zyVersion ...> ... </zyVersion>" substring (from escaped input).
 * - rawContent: the inner text between the opening and closing zyVersion tags.
 * - elementsWithId: tag name and id for every descendant element under zyVersion that has an id.
 * - ancestorWithId: when elementsWithId is empty, the nearest ancestor with an id attribute.
 *
 * Nested `<zyVersion>` blocks are supported; each block is one entry with correctly bounded inner XML.
 */
export function getZyVersionsFromRawZyXml(rawZyXml: string): ZyVersionFromXml[] {
    const commentRemoved = removeZyComments(rawZyXml);
    const entitiesResolved = removeAllHtmlNamedEntities(commentRemoved);
    const urlAttrAmpersandsOk = escapeBareAmpersandsInXmlAttributeValues(entitiesResolved, [
        'link',
        'src',
        'href',
        'shorttitle',
        'title',
        'caption',
        'alt',
        'name',
    ]);
    const cleanedXml = removeTagContent(urlAttrAmpersandsOk, [
        'zyTool',
        'zyAnimator',
        'zyICode',
        'zyInlineCode',
        'zyCode',
        'zyProgram',
        'zyStudentProgram',
        'zyPyTutorCode',
        'zyQTextAroundAns',
        'zyProblemSolution',
        'zySolution',
        'zyTestVectors',
        'zyLatexEquation',
    ]);
    const zyversions = extractZyVersionSpans(cleanedXml);

    // No zyVersion blocks found, return empty array
    if (zyversions.length === 0) return [];

    // Parse the full escaped XML once so we can walk the ancestor chain for text-only blocks.
    const domParser = new DOMParser();
    const xmlDoc = domParser.parseFromString(`<_root>${cleanedXml}</_root>`, 'text/xml');
    const parsingError = xmlDoc.querySelector('parsererror');
    if (parsingError) {
        throw new Error(`Error getZyVersionsFromRawZyXml: ${parsingError.textContent}`);
    }

    const zyVersionElements = xmlDoc.querySelectorAll('zyVersion');
    return zyversions.map(({ rawTagAndContent, attrsString, rawContent }, i) => {
        const versionMatch = /\bversion="([^"]*)"/i.exec(attrsString);
        const version = versionMatch ? versionMatch[1] : '';
        const elementsWithId = parseElementsWithId(rawContent);
        const result: ZyVersionFromXml = { version, rawTagAndContent, rawContent, elementsWithId };

        if (elementsWithId.length === 0) {
            const zyVersionEl = zyVersionElements[i];
            let parent = zyVersionEl?.parentElement;
            while (parent && parent.tagName !== '_root') {
                const id = parent.getAttribute('id');
                if (id !== null) {
                    result.ancestorWithId = { elementName: parent.tagName, id };
                    break;
                }
                parent = parent.parentElement;
            }
        }

        return result;
    });
}

/**
 * Strips a known zyVersion suffix (e.g. "cpp", "java") from the end of a content id.
 *
 * The backend appends the version string directly to the id when a resource lives inside
 * a <zyVersion> block (e.g. "4161ddda-...cpp"). Pass in the list of known version strings
 * so the function can find and remove the correct one.
 *
 * Versions are tried longest-first to prevent a short version like "c" from incorrectly
 * matching the tail of "cpp".
 *
 * Returns the original id unchanged when no known version suffix is found.
 */
export function stripZyVersionSuffix(id: string, knownVersions: string[]): string {
    const sorted = [...knownVersions].sort((a, b) => b.length - a.length);
    for (const version of sorted) {
        if (version && id.endsWith(version)) {
            return id.slice(0, -version.length);
        }
    }
    return id;
}

/**
 * Resolves the zyVersionIdMap entry for a given node id.
 *
 * @param nodeId - The node id to resolve the zyVersionIdMap entry for.
 * @param map - ZyVersionIdMap
 * @param versions - a reduced map of versions and any ids that are valid for that version
 * @returns The zyVersionIdMap entry for the given node id.
 */
export function resolveZyVersionEntry(
    nodeId: string,
    map: Record<string, ZyVersionIdEntry>,
    versions: Record<string, string[]>,
): ZyVersionIdEntry | undefined {
    if (!nodeId) return undefined;

    const direct = map[nodeId];
    if (direct) return direct;

    // double-suffix, make sure we don't over strip the version suffix
    for (const version of Object.keys(versions)) {
        if (!nodeId.endsWith(version)) continue;
        const validIds = versions[version];
        const slicedNodeId = nodeId.slice(0, -version.length * 2);
        if (validIds.includes(slicedNodeId)) {
            return map[nodeId.slice(0, -version.length)];
        }
    }
    return undefined;
}

/**
 * Builds a plain object map from every known id in a zyVersion array to its lookup entry.
 *
 * Map keys are the element id suffixed with the version string (commas and spaces removed),
 * matching the format the backend uses when it builds content-resource GUIDs:
 *   e.g. id "4161ddda-..." with version "c, cpp"  →  key "4161ddda-...ccpp"
 *
 * Keys come from two sources:
 * - elementsWithId ids  → type "directChild"
 * - ancestorWithId.id   → type "ancestor"
 *
 * When the same key appears as both inside a zyVersion subtree (in one block) and as an ancestor
 * (in another), the in-version entry takes precedence — it is a tighter match.
 */
export function buildZyVersionIdMap(zyVersions: ZyVersionFromXml[]): Record<string, ZyVersionIdEntry> {
    const map: Record<string, ZyVersionIdEntry> = {};

    for (let index = 0; index < zyVersions.length; index++) {
        const { version, elementsWithId, ancestorWithId } = zyVersions[index];
        const versionSuffix = version.replace(/[,\s]/g, '');

        for (const el of elementsWithId) {
            const key = `${el.id}${versionSuffix}`;
            const existing = map[key];
            if (existing && existing.type === 'directChild') {
                console.warn(
                    `buildZyVersionIdMap: key "${key}" appears under multiple zyVersion blocks (indices ${existing.index} and ${index}). Keeping first.`,
                );
                continue;
            }
            map[key] = { type: 'directChild', index, version };
        }

        if (ancestorWithId) {
            const key = `${ancestorWithId.id}${versionSuffix}`;
            const existing = map[key];
            if (!existing) {
                map[key] = { type: 'ancestor', index, version };
            } else if (existing.type === 'ancestor') {
                console.warn(
                    `buildZyVersionIdMap: key "${key}" is an ancestor of multiple zyVersion blocks (indices ${existing.index} and ${index}). Keeping first.`,
                );
            }
            // directChild always wins over ancestor — no action needed if existing.type === 'directChild'
        }
    }

    return map;
}

function parseElementsWithId(rawContent: string): Array<{ elementName: string; id: string }> {
    if (!rawContent.trim()) return [];

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(`<_r>${rawContent}</_r>`, 'text/xml');

    if (xmlDoc.querySelector('parsererror')) {
        throw new Error(`Error parsing zyxml in parseElementsWithId: ${xmlDoc.querySelector('parsererror')?.textContent}`);
    }

    const root = xmlDoc.documentElement;
    const withId = root.querySelectorAll('[id]');
    const out: Array<{ elementName: string; id: string }> = [];

    for (let i = 0; i < withId.length; i++) {
        const el = withId[i];
        const id = el.getAttribute('id');
        if (id !== null && id !== '') {
            out.push({ elementName: el.tagName, id });
        }
    }

    return out;
}

function isImageAttributeWithFilename(o: unknown): o is Record<string, unknown> {
    if (!o || typeof o !== 'object' || Array.isArray(o)) {
        return false;
    }
    const r = o as Record<string, unknown>;
    return 'filename' in r && 'image_ids' in r;
}

/**
 * Sets `subject` and `release_id` on every image attribute object (those with `filename` and
 * `image_ids`) under any `attributes` array in the section tree, for sandbox / static JSON.
 */
export function enrichImageAttributesInSection(section: unknown, { subject, releaseId }: { subject: string; releaseId: string }): void {
    if (section === null || section === undefined) {
        return;
    }
    if (Array.isArray(section)) {
        for (const item of section) {
            enrichImageAttributesInSection(item, { subject, releaseId });
        }
        return;
    }
    if (typeof section !== 'object') {
        return;
    }
    const o = section as Record<string, unknown>;
    for (const key of Object.keys(o)) {
        const v = o[key];
        if (key === 'attributes' && Array.isArray(v)) {
            for (const a of v) {
                if (isImageAttributeWithFilename(a)) {
                    a.subject = subject;
                    a.release_id = releaseId;
                }
            }
        } else {
            enrichImageAttributesInSection(v, { subject, releaseId });
        }
    }
}

/**
 * For every `short_answer` content resource, sets `payload.release_id` and, when the converter
 * `subject` is non-empty, `payload.subject`, so they can be read from `content_resources[].payload`.
 */
export function prepareSectionForImportConversion(
    section: unknown,
    { subject, releaseId }: { subject: string; releaseId: string },
): Record<string, unknown> {
    const prepared = JSON.parse(JSON.stringify(section)) as Record<string, unknown>;
    enrichImageAttributesInSection(prepared, { subject, releaseId });
    enrichShortAnswerResourcePayloads(prepared, { subject, releaseId });
    return prepared;
}

export function enrichShortAnswerResourcePayloads(
    section: Record<string, unknown>,
    { subject, releaseId }: { subject: string; releaseId: string },
): void {
    const crs = section.content_resources;
    if (!Array.isArray(crs)) {
        return;
    }
    for (const cr of crs) {
        if (!cr || typeof cr !== 'object' || (cr as Record<string, unknown>).type !== 'short_answer') {
            continue;
        }
        const p = (cr as Record<string, unknown>).payload;
        if (!p || typeof p !== 'object' || p === null || Array.isArray(p)) {
            continue;
        }
        const payload = p as Record<string, unknown>;
        if (subject.trim() !== '') {
            payload.subject = subject;
        }
        payload.release_id = releaseId;
    }
}

export async function convertZyXml(
    zyXml: string,
    outputFormat: 'html' | 'json',
    sectionMetadata?: Record<string, any>,
): Promise<{ section: any; output: JSONContent | string } | null> {
    const zyxmlParseResponse = await zyxmlToJson(zyXml.trim());
    if (zyxmlParseResponse.error || zyxmlParseResponse.errors) {
        console.error('Error parsing zyXML in convertZyXml:', zyxmlParseResponse.error?.message);
        return null;
    }

    if (!zyxmlParseResponse.sections.length) {
        console.error('No sections found in the ZyXML content.');
        return null;
    }

    // Add zyversions to section metadata
    const zyversions = getZyVersionsFromRawZyXml(zyXml);
    const zyversionIdMap = buildZyVersionIdMap(zyversions);

    return convertParseResponseSection(zyxmlParseResponse.sections[0], outputFormat, {
        ...sectionMetadata,
        zyversions,
        zyversionIdMap,
    });
}

export async function convertParseResponseSection(
    zyxmlParseResponseSection: ZyXmlToJsonResponse['sections'][0],
    outputFormat: 'html' | 'json',
    sectionMetadata?: Record<string, any>,
): Promise<{ section: any; output: JSONContent | string } | null> {
    const section = JSON.parse(
        JSON.stringify({
            ...zyxmlParseResponseSection,
            ...sectionMetadata,
        }),
    );

    return {
        section: section,
        output: outputFormat === 'html' ? await zybookSectionToHTML(section) : await zybookSectionToProseMirrorJson(section),
    };
}

export async function zybookSectionToHTML(section: any): Promise<string> {
    const { content_resources } = section;
    const htmlPromises = content_resources.map(async (resource: ContentResource) => {
        let html = await zybookContentResourceToHTML({
            ...resource,
            sectionMetadata: section,
        });
        if (html === null) {
            return '';
        }
        if (containsZyCode(html)) {
            html = await migrateZyCodeFormat(html, resource);
        }
        return html;
    });
    const htmlResults = await Promise.all(htmlPromises);
    return htmlResults.join('');
}

/**
 * Convert zylink `<zyLink link="…">…</zyLink>` tags to `<a href="…">…</a>` so TipTap's
 * link mark can parse them. Needed for footnote bodies, where zyAuthor's print_node path
 * passes zyLink through as literal HTML instead of attributed-string link parts.
 */
export function convertZyLinkTagsToAnchors(html: string): string {
    return html.replace(/<zyLink\b([^>]*)>([\s\S]*?)<\/zyLink>/gi, (_match, attrs: string, inner: string) => {
        const linkMatch = attrs.match(/\blink\s*=\s*"([^"]*)"/i) ?? attrs.match(/\blink\s*=\s*'([^']*)'/i);
        const href = linkMatch?.[1] ?? '';
        return `<a href="${href}">${inner}</a>`;
    });
}

/**
 * Process footnotes in HTML by converting footnote anchors into zyFootnote elements
 * with embedded reference content.
 */
function processFootnotesInHtml(html: string, footnoteReferences?: Map<string, string>): string {
    if (!footnoteReferences || footnoteReferences.size === 0) {
        return html;
    }

    // Match footnote anchor pattern: <sup id="xxx-anchor" class="footnote-anchor">name</sup>
    return html.replace(/<sup\s+id="([^"]+)-anchor"\s+class="footnote-anchor">(\d+)<\/sup>/g, (match, id, name) => {
        const referenceHtml = footnoteReferences.get(id);
        if (!referenceHtml) {
            // If no reference found, return the original anchor
            return match;
        }

        // Extract the reference content (everything inside the <p> tag)
        const referenceMatch = referenceHtml.match(/<p[^>]*class="footnote-reference"[^>]*>(.*?)<\/p>/s);
        if (!referenceMatch) {
            return match;
        }

        // Remove the (*name) prefix from reference content
        const referenceContent = convertZyLinkTagsToAnchors(referenceMatch[1].replace(/^\(\*\d+\)\s*/, '').trim());

        // Create a zyFootnote element with the reference as content
        return `<zyFootnote id="${id}" name="${name}">${referenceContent}</zyFootnote>`;
    });
}

export async function zybookSectionToProseMirrorJson(section: Section): Promise<JSONContent> {
    const { content_resources, ...sectionMetadata } = section;
    const zyversionIdMap: Record<string, ZyVersionIdEntry> = sectionMetadata.zyversionIdMap;
    const reducedVersions =
        sectionMetadata.zyversions?.reduce((acc: Record<string, string[]>, zyversion: ZyVersionFromXml) => {
            const version = zyversion.version.replace(/[,\s]/g, '');
            const elementsWithId = zyversion.elementsWithId.map((element: { id: string }) => element.id);
            const ancestorWithId = zyversion.ancestorWithId?.id;
            if (!acc[version]) {
                acc[version] = elementsWithId.concat(ancestorWithId ? [ancestorWithId] : []);
            } else {
                acc[version] = acc[version].concat(elementsWithId.concat(ancestorWithId ? [ancestorWithId] : []));
            }
            return acc;
        }, {}) || {};
    // First pass: collect footnote references
    const footnoteReferences = new Map<string, string>();
    content_resources.forEach(resource => {
        if (resource.type === 'html' && typeof resource.payload.html === 'string') {
            const html = resource.payload.html;
            // Check if this is a footnote reference (contains footnote-reference class)
            if (html.includes('class="footnote-reference"')) {
                // Extract the footnote ID from the HTML
                const idMatch = html.match(/id="([^"]+)-reference"/);
                if (idMatch) {
                    footnoteReferences.set(idMatch[1], html);
                    return; // Skip this resource, it will be merged with the anchor
                }
            }
        }
    });

    const docJson: JSONContent = {
        type: 'doc',
        attrs: {
            title: {
                default: sectionMetadata?.title,
            },
            sectionId: sectionMetadata?.id,
            zybookCode: sectionMetadata?.devBookName ?? sectionMetadata?.zybook_code ?? null,
            chapterNumber: sectionMetadata?.chapter_number,
            sectionNumber: sectionMetadata?.section_number,
        },
        content: [],
    };

    // Second pass: convert content resources to ProseMirror JSON
    const htmlPromises = content_resources.map(async (resource: ContentResource) => {
        const contentResourceJson = await zybookContentResourceToProseMirrorJson({
            ...resource,
            sectionMetadata,
            footnoteReferences,
        });

        const hasContent = contentResourceJson && contentResourceJson.content && contentResourceJson.content.length && docJson.content;
        if (!hasContent) {
            return contentResourceJson;
        }

        // If GUID exists, use it to patch unique IDs for nodes
        if (resource.guid && resource.type === 'html') {
            const nodeId = getNodeIdFromGuid(resource.guid);
            const content = contentResourceJson.content ?? [];
            const hasMultipleNodes = content.length > 1;
            contentResourceJson.content = content.map((node: any, index: number) => {
                // Only modify paragraph nodes
                if (node.type !== 'paragraph') {
                    return node;
                }

                let id = nodeId;
                if (hasMultipleNodes && index < content.length - 1) {
                    id = uuidv4();
                }
                node.attrs = {
                    ...node.attrs,
                    id,
                };
                return node;
            });
        }

        // Wrap the content in the zyVersion tag
        // Only apply if zyVersion is a top level tag
        if (resource.guid && zyversionIdMap) {
            const nodeId = getNodeIdFromGuid(resource.guid);
            const zyversionEntry = resolveZyVersionEntry(nodeId, zyversionIdMap, reducedVersions);
            if (zyversionEntry) {
                const isDirectChild = zyversionEntry.type === 'directChild';

                // If the zyVersion is a direct child, wrap the content in the zyVersion tag
                if (isDirectChild) {
                    contentResourceJson.content = [
                        {
                            type: 'zyVersionBlock',
                            attrs: {
                                version: normalizeVersion(zyversionEntry.version),
                            },
                            content: contentResourceJson.content,
                        },
                    ];
                }
            }
        }
        return contentResourceJson;
    });

    const htmlResults = await Promise.all(htmlPromises);
    htmlResults.map(contenResourceJson => {
        docJson.content = docJson.content?.concat(contenResourceJson?.content || []);
    });

    return docJson;
}

async function zybookContentResourceToHTML(resource: ContentResource): Promise<string | null> {
    switch (resource.type) {
        case 'markdown': {
            const markdown = resource.payload.markdown;
            if (typeof markdown !== 'string' || !markdown.trim()) {
                return '';
            }
            return renderMarkdownToHtml(markdown);
        }

        case 'html':
            let html: string; // eslint-disable-line no-case-declarations

            if (typeof resource.payload.html === 'string') {
                html = resource.payload.html;
            } else if (
                Array.isArray(resource.payload.html) &&
                resource.payload.html.some((item: any) => item.text && item.text.includes('class="explore"'))
            ) {
                // Handle zyExplore content
                return await convertZyExploreToHtml(resource);
            } else if (Array.isArray(resource.payload.html)) {
                html = extractHtml(resource.payload.html, resource);
            } else {
                // Handle edge case where html is not a string or array
                return await convertUnsupportedToHtml(resource);
            }

            // If there are no footnote references, return the HTML as is
            if (!resource.footnoteReferences || resource.footnoteReferences.size === 0) {
                return html;
            }

            // Handle footnotes
            if (html.includes('class="footnote-anchor"')) {
                return processFootnotesInHtml(html, resource.footnoteReferences);
            } else if (html.includes('class="footnote-reference"')) {
                return null; // Footnote references are handled with anchors
            }

            return html;

        case 'image':
            return convertImageToHtml(resource);

        case 'container':
            return await convertContainerToHtml(resource);

        case 'short_answer':
            return await convertShortAnswerToHtml(resource);

        case 'multiple_choice':
            return convertMultipleChoiceToHtml(resource);

        case 'detect_answer':
            return convertDetectAnswerToHtml(resource);

        case 'exercise':
            return convertExerciseToHtml(resource);

        case 'custom':
            // Handle custom types like defnMatch
            switch (resource.payload.tool) {
                case 'defnMatch':
                    return convertMatchingToHtml(resource);
                case 'zyAnimator':
                    return convertZyAnimatorToHtml(resource);
                case 'ProgressionPlayer':
                case 'ProgressionPlayerInteractiveAndAccessible':
                    return convertProgressionPlayerToHtml(resource);
                case 'BlockOrderingPA':
                case 'BlockOrderingCA':
                case 'BlockOrderingPA2Column':
                case 'BlockOrderingCA2Column':
                case 'block-ordering-ca':
                case 'block-ordering-pa':
                case 'block-ordering-ca2-column':
                case 'block-ordering-pa2-column':
                    return convertBlockOrderingToHtml(resource);
                case 'CodeWriting':
                    return convertCodeWritingToHtml(resource);
                case 'codeOutput':
                    return convertCodeOutputToHtml(resource);
                case 'ParsonsCodingPA':
                    return convertParsonsCodingToHtml(resource);
                case 'OpenStaxPythonCodeRunnerWrapper':
                    return convertOpenStaxPythonCodeRunnerBuilderToHtml(resource);
                case 'StreamlitApp':
                    return convertStreamlitAppToHtml(resource);
                case 'zyde':
                    return convertZyDEToHtml(resource);
                default:
                    return await convertZyToolToHtml(resource);
            }
        case 'video':
            return convertZyYoutubeVideoToHtml(resource);

        case 'lti':
            return convertMathWorksLearningToolToHtml(resource);

        case 'zystudio':
            return convertZyStudioToHtml(resource);

        case 'programming_submission':
            return convertZyClassicLabToHtml(resource);

        case 'itlab':
            return convertZyITLabToHtml(resource);

        default:
            return await convertUnsupportedToHtml(resource);
    }
}

async function zybookContentResourceToProseMirrorJson(resource: ContentResource): Promise<JSONContent> {
    let html = await zybookContentResourceToHTML(resource);
    if (html === null) {
        return {};
    }

    if (containsZyVersion(html)) {
        html = migrateZyVersionFormat(html);
    }

    html = migrateZyFunction(html);
    html = migrateZyFctNameHtml(html);

    // required so during import flow we can accurately determine if a
    // zydefn node should be inline or block
    html = markZydefnBlocksInHtml(html);

    if (containsZyCode(html)) {
        html = await migrateZyCodeFormat(html, resource);
    }

    if (containsZyConsole(html)) {
        html = await migrateZyConsoleFormat(html);
    }

    if (containsZyCommonError(html)) {
        html = await migrateZyCommonErrorFormat(html);
    }

    if (containsZyGoodPractice(html)) {
        html = await migrateZyGoodPracticeFormat(html);
    }

    if (containsZyReference(html)) {
        html = migrateZyReferenceFormat(html);
    }

    if (containsZyProof(html)) {
        html = await migrateZyProofFormat(html);
    }

    if (containsLegacyZyMatrixHtml(html)) {
        html = migrateLegacyZyMatrixFormat(html);
    }

    if (containsCodeBlock(html)) {
        html = migrateCodeBlockFormat(html);
    }

    return generateJSON(html, getExtensions(null, {}, ['trailingNode']));
}

function convertImageToHtml(resource: ContentResource): string {
    if (!resource.payload?.image_ids) {
        return '';
    }
    const { sectionMetadata } = resource;
    const subject = resolveImageSubjectName(sectionMetadata?.subject) || 'zybookDemoSubject';
    const releaseId = resolveImageReleaseId(sectionMetadata?.releaseId) || resolveImageReleaseId(sectionMetadata?.release_id) || '1';
    return generateImgTag({ text: '', attributes: [{ type: 'image', ...resource.payload }] }, { subject, releaseId });
}

function convertExerciseToHtml(resource: ContentResource): string {
    // Extract attributes for zyExercise
    const nodeId = getNodeIdFromGuid(resource.guid);
    const exerciseAttrs = {
        id: nodeId || '',
        caption: resource.caption || '',
    };

    // Build zyExercise opening tag
    let html = `<zyExercise`;
    Object.entries(exerciseAttrs).forEach(([key, value]) => {
        html += ` ${key}="${value}"`;
    });
    html += '>\n';

    // Add zySetup
    if (resource.payload.setup) {
        html += '<zySetup>';
        const setupContent = extractHtml(resource.payload.setup, resource);
        html += setupContent;
        html += '</zySetup>\n';
    }

    // Add zyProblem elements
    resource.payload.questions.forEach((question: any) => {
        html += '\n<zyProblem>';

        // Add problem text
        const problemContent = extractHtml(question.text, resource);
        html += problemContent;

        // Add solution if exists
        if (question.solution && question.solution.length > 0) {
            const studentView = question.student_view ? 'true' : 'false';
            html += `\n<zyProblemSolution studentview="${studentView}">`;
            const solutionContent = extractHtml(question.solution, resource);
            html += solutionContent;
            html += '</zyProblemSolution>';
        }

        html += '\n</zyProblem>\n';
    });

    html += '\n</zyExercise>';

    return html;
}

async function convertContainerToHtml(resource: ContentResource) {
    if (['aside-bordered', 'aside-elaboration', 'aside-chapteropener', 'aside-block'].includes(resource.payload.type?.toLowerCase())) {
        return convertAsideToHtml(resource);
    }

    if (resource.payload.name === 'Construct') {
        resource.payload.name = 'Figure';
    }

    if (['Example', 'Figure', 'Table', 'Try'].includes(resource.payload.name)) {
        return convertZyExampleToHtml(resource);
    }
    resource.payload.enumType = resource.payload.name;
    resource.payload.name = 'Enum';
    return convertZyExampleToHtml(resource);
}

function convertZyExploreToHtml(resource: ContentResource): string {
    const payload = resource.payload;
    return `<zyExplore id="${payload.ref}">${extractHtml(payload.html, resource).replace('Exploring further:', '')}</zyExplore>`;
}

function convertAsideToHtml(resource: ContentResource): string {
    const payload = resource.payload;
    const htmlText = extractHtml(payload.html, resource);
    let type = payload.type.toLowerCase() ? payload.type.toLowerCase().replace('aside-', '') : 'elaboration';

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const textContent = doc.body?.textContent?.trim() ?? '';

    // Normalize to 'learning objective' when: type is bordered with learning-objective content/name, or name is "Learning objectives" (so preview uses aside-bordered styling).
    if (
        (type === 'bordered' && (textContent.startsWith('Learn to:') || payload.name === 'Learn to:')) ||
        payload.name === 'Learning objectives'
    ) {
        type = 'learning objective';
    }

    return `<zyAside id="${payload.ref}" name="${payload.name}" type="${type}">${htmlText}</zyAside>`;
}

async function convertUnsupportedToHtml(resource: ContentResource, tryRetrieveXML = true): Promise<string> {
    let id = '';
    if (resource.guid) {
        const nodeId = getNodeIdFromGuid(resource.guid);
        id = nodeId;
    } else if (resource.payload && resource.payload.ref) {
        id = resource.payload.ref;
    }

    if (tryRetrieveXML) {
        const rawXml = await getXmlFromResource(resource);
        if (rawXml) {
            return `<unsupported-editable id="${id}">${escapeXml(rawXml)}</unsupported-editable>`;
        }
        if (id || resource.guid) {
            return onMissingZytagSnippet(resource);
        }
    }
    return `<unsupported id="${id}">${escapeXml(JSON.stringify(resource))}</unsupported>`;
}

/**
 * Converts a zyBooks example resource into an HTML string wrapped in a custom <zyExample> element.
 *
 * @param resource - The zyBooks content resource to convert, containing payload and metadata.
 * @returns The HTML string representation of the zyBooks example, suitable for rendering.
 */
function convertZyExampleToHtml(resource: ContentResource): string {
    const resourceType = resource.payload.name.toLowerCase() || 'example';
    const additionalAttrs: Record<string, any> = {};

    let htmlString: string;
    if (Array.isArray(resource.payload.html)) {
        const htmlArray = resource.payload.html.map((item: any) => {
            // Process the image content
            if (!item.text && item.attributes[0].image_ids) {
                const subject = resource.sectionMetadata?.subject || 'QuantitativeReasoning';
                const releaseId = resource.sectionMetadata?.releaseId || '1';
                item.text = generateImgTag(item, { subject, releaseId });
                return item;
            }
            return item;
        });
        htmlString = extractHtml(htmlArray, resource);
    } else {
        htmlString = resource.payload.html;
    }

    if (resourceType === 'enum') {
        additionalAttrs.enumType = resource.payload.enumType;
    }

    const additionalAttrsString = Object.entries(additionalAttrs)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');

    const nodeId = getNodeIdFromGuid(resource.guid);
    return `<zyExample id="${nodeId || ''}" caption="${resource.caption || ''}" type="${resourceType}" ${additionalAttrsString}>${htmlString}</zyExample>`;
}

function resolveImageSubjectName(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (value && typeof value === 'object') {
        const subject = (value as Record<string, unknown>).content_source;
        if (typeof subject === 'string') {
            return subject;
        }
    }

    return '';
}

function resolveImageReleaseId(value: unknown): string {
    if (typeof value === 'string' || typeof value === 'number') {
        return `${value}`;
    }

    if (value && typeof value === 'object') {
        const v = value as Record<string, unknown>;
        const releaseId = v.releaseId ?? v.release_id;
        if (typeof releaseId === 'string' || typeof releaseId === 'number') {
            return `${releaseId}`;
        }
    }

    return '';
}

function extractHtml(htmlArray: zybookHTMLContent[], resource?: ContentResource): string {
    if (!htmlArray || htmlArray.length === 0) return '';

    return htmlArray
        .map(item => {
            const firstAttribute = item.attributes?.[0];
            const imageAttrs = firstAttribute as Record<string, any> | undefined;
            const hasReferenceAttribute =
                Array.isArray(item.attributes) && item.attributes.some(attribute => attribute?.type === 'reference');
            const isImage = imageAttrs?.type === 'image' || imageAttrs?.type === 'inline-image';
            const isLink = firstAttribute?.type === 'link';

            if (isImage && imageAttrs?.image_ids?.length) {
                // https://zytools.zybooks.com/zyAuthor/LinearAlgebra/64/IMAGES/5a753b0b-b8d7-4870-8a0a-21c77376ad3a
                // Construct the image URL to match the example format
                const subject =
                    resolveImageSubjectName(imageAttrs.subject) ||
                    resolveImageSubjectName(resource?.sectionMetadata?.subject) ||
                    'ExampleZybookCode';
                const releaseId =
                    resolveImageReleaseId(imageAttrs.release_id) ||
                    resolveImageReleaseId(imageAttrs.releaseId) ||
                    resolveImageReleaseId(resource?.sectionMetadata?.releaseId) ||
                    resolveImageReleaseId(resource?.sectionMetadata?.release_id) ||
                    '1';
                return generateImgTag(item, { subject, releaseId });
            }

            // Handle inline-enum type
            if (item.attributes?.[0]?.type === 'inline-enum') {
                return convertInlineEnumToHtml(item);
            }

            // Handle iframe type
            if (item.attributes?.[0]?.type === 'iframe') {
                return convertIframeFromHtmlContent(item as ZyIframeHtmlContent);
            }

            // Handle link type
            if (isLink) {
                return `<a href="${item.href}">${item.text}</a>`;
            }

            // zyauthor-server may return references as attributed text objects with empty text.
            // Emit an HTML element that preserves the target ID so it can be migrated to a reference node.
            if (hasReferenceAttribute && item.reference && item.guid) {
                const targetId = item.reference;
                const sourceId = item.guid;
                const sourceSectionId = resource?.sectionMetadata?.id || '';
                return `<zyReference id="${sourceId}" target_id="${targetId}" section_id="${sourceSectionId}">REFERENCE</zyReference>`;
            }

            if (resource?.footnoteReferences && item.text?.includes('class="footnote-anchor"')) {
                return processFootnotesInHtml(item.text, resource.footnoteReferences);
            }

            let text = item.text ?? '';

            // Clean inline LaTeX expressions
            text = cleanInlineLatex(text);

            // Workaround for malformed block math
            if (text.includes('\\(\\)')) {
                text = cleanMalformedBlockMath(text);
            }

            return text;
        })
        .join('');
}

function convertInlineEnumToHtml(item: zybookHTMLContent): string {
    const enumId = item.enumid;
    const type = item.text.startsWith('$$') ? 'LaTex' : 'MathML';
    const content = type === 'LaTex' ? item.text.slice(2, -2).trim() : item.text;
    return type === 'LaTex'
        ? `<zyInlineEnum id="${enumId}" type="${type}"><div data-type="block-math" data-${type.toLowerCase()}="${content}"></div></zyInlineEnum>`
        : `<zyInlineEnum id="${enumId}" type="${type}">${content}</zyInlineEnum>`;
}

export function convertQuestionSetInstructionsToHtml(resource: ContentResource): string {
    if (!resource.instructions || resource.instructions.length === 0) {
        return '<div class="instructions"><p></p></div>';
    }
    const part = extractHtml(resource.instructions, resource);
    const isEmpty = resource.instructions.length === 1 && part.trim() === '';
    return `<div class="instructions">${isEmpty ? '<p></p>' : part}</div>`;
}

export async function convertShortAnswerToHtml(resource: ContentResource): Promise<string> {
    let html = '';

    // Add instructions if present
    html += convertQuestionSetInstructionsToHtml(resource);

    // Convert each question
    for (let index = 0; index < resource.payload.questions.length; index++) {
        const question = resource.payload.questions[index];
        const questionText = extractHtml(question.text, resource);

        // Extract correct answers (could be string, array, or multiple answers)
        let correctAnswers: string[] = [];
        let preferredAnswer: string | null = null;
        let preferredIndex: number | null = null;
        let answerWrapper: { content: string; language: string } | null = null;

        // Check for 'answers' field first (array of strings)
        if (question.answers && Array.isArray(question.answers)) {
            correctAnswers = question.answers;
            // Check for preferred answer
            if (question.preferred != null) {
                preferredAnswer = question.preferred;
            }
        }
        // Fall back to 'answer' field for backward compatibility
        else if (question.answer) {
            if (typeof question.answer === 'string') {
                correctAnswers = [question.answer];
            } else if (Array.isArray(question.answer)) {
                // Check if it's an array of strings (multiple answers) or HTMLContent array
                if (question.answer.length > 0 && typeof question.answer[0] === 'string') {
                    correctAnswers = question.answer;
                } else {
                    // It's HTMLContent array, extract the HTML
                    const extractedAnswer = extractHtml(question.answer, resource);
                    correctAnswers = [extractedAnswer];
                }
            } else if (question.answer.text) {
                correctAnswers = [extractHtml(question.answer.text, resource)];
            }
        }

        // Check if we need to create an answer wrapper (text_before or text_after present)
        if (question.text_before || question.text_after) {
            const textBefore = question.text_before || '';
            const textAfter = question.text_after || '';

            let wrapperContent = '';
            let language = 'plaintext';

            try {
                const rawXml = await getXmlFromResource(resource);
                const zyQTextAroundAnsRegex = /<zyQTextAroundAns([^>]*)>([\s\S]*?)<\/zyQTextAroundAns>/i;
                const match = rawXml?.match(zyQTextAroundAnsRegex);
                if (match && match[2]) {
                    const openTagAttrs = match[1] ?? '';
                    const langMatch = openTagAttrs.match(/language=["']([^"']*)["']/i);
                    language = langMatch ? langMatch[1].trim() : detectLanguageFromCodeSnippet(match[2]);
                    wrapperContent = match[2].trim();
                    wrapperContent = wrapperContent.replace('<zyQAnsBox></zyQAnsBox>', '{{ANSWER}}');
                }
            } catch (error) {
                console.error(`convertShortAnswerToHtml error: ${error}`);
            }

            // If no raw content found, combine text_before + answer + text_after
            if (!wrapperContent) {
                const combinedText = `<div class="code"><pre>${textBefore}{{ANSWER}}${textAfter}</pre></div>`;
                wrapperContent = simpleConvertZyCode(combinedText, resource, true);
                language = detectLanguageFromCodeSnippet(combinedText);
            }

            answerWrapper = {
                content: wrapperContent,
                language: language,
            };
        }

        // Default to single empty answer if no answers found
        if (correctAnswers.length === 0) {
            correctAnswers = [''];
        }

        if (preferredAnswer != null) {
            const matchIndex = correctAnswers.indexOf(preferredAnswer);
            if (matchIndex >= 0) {
                preferredIndex = matchIndex;
            }
        }

        const columns = question.columns || null;
        const lines = question.lines ?? question.rows ?? null;
        const obeySpace = question.obey_space || false;
        const obeyCase = question.obey_case || false;
        const obeyQuote = question.obey_quote ?? true;
        const obeyLinear = question.obey_linear || false;
        const assessmentType = question.assessment_type ?? 'string';
        const notation = question.notation != null && question.notation !== '' ? String(question.notation) : '';
        const ignoreEndRaw = question.ignoreend != null ? String(question.ignoreend) : '';
        const extraAssessmentOptions = shortAnswerQuestionExtraAssessmentOptions(question as Record<string, unknown>);
        const assessmentOptionsPayload: Record<string, unknown> = {
            ...extraAssessmentOptions,
            ...(notation ? { notation } : {}),
        };
        if (lines != null) {
            const ln = Number(lines);
            if (!Number.isNaN(ln)) {
                assessmentOptionsPayload.lines = ln;
            }
        }
        if (columns != null) {
            const cn = Number(columns);
            if (!Number.isNaN(cn)) {
                assessmentOptionsPayload.columns = cn;
            }
        }
        assessmentOptionsPayload.obeyspace = obeySpace;
        assessmentOptionsPayload.obeycase = obeyCase;
        assessmentOptionsPayload.obeyquote = obeyQuote;
        assessmentOptionsPayload.obeylinear = obeyLinear;
        if (ignoreEndRaw) {
            assessmentOptionsPayload.ignoreend = ignoreEndRaw;
        }

        const importedToolLabel = getShortAnswerToolLabelByProperties(
            attrsToBuilderProps({
                assessmentType,
                assessmentOptions: assessmentOptionsPayload,
            }),
        );
        const assessmentTypeAttr = ` data-assessment-type="${escapeXml(String(assessmentType))}"`;
        const assessmentOptionsAttr =
            Object.keys(assessmentOptionsPayload).length > 0
                ? ` data-assessment-options="${escapeXml(encodeURIComponent(JSON.stringify(assessmentOptionsPayload)))}"`
                : '';
        // Start resource container
        html += `<div data-title="Short Answer ${index + 1}" data-type="short-answer" class="short-answer-container">`;

        // Add question container
        html += `<div data-type="short-answer-question" class="short-answer-question-container">`;
        html += questionText;
        html += `</div>`;

        // Add answer container
        const preferredIndexAttr = preferredIndex != null ? ` data-preferred-index="${preferredIndex}"` : '';
        html += `<div data-type="short-answer-answer" data-tool-label="${escapeXml(importedToolLabel)}" data-answers="${escapeXml(JSON.stringify(correctAnswers))}"`;
        html += `${preferredIndexAttr}${assessmentTypeAttr}${assessmentOptionsAttr}>`;
        html += `</div>`;

        // Add answer wrapper if present
        if (answerWrapper) {
            html += `<div data-type="short-answer-answer-wrapper" data-content="${escapeXml(answerWrapper.content)}" data-language="${answerWrapper.language}">`;
            html += `</div>`;
        }

        // Add hint if present
        let hintContent = '';
        if (question.hint) {
            if (typeof question.hint === 'string') {
                hintContent = question.hint;
            } else if (Array.isArray(question.hint)) {
                hintContent = extractHtml(question.hint, resource);
            }
        }
        html += `<div data-type="short-answer-hint">`;
        html += `<p>${hintContent}</p>`;
        html += `</div>`;

        // Add explanation if present
        // Some resources might use 'solution' instead of 'explanation'
        let explanationContent = '';
        const explanation = question.explanation || question.solution;
        if (explanation) {
            if (typeof explanation === 'string') {
                explanationContent = explanation;
            } else if (Array.isArray(explanation)) {
                explanationContent = extractHtml(explanation, resource);
            }
        }
        html += `<div data-type="short-answer-explanation">`;
        html += `<p>${explanationContent}</p>`;
        html += `</div>`;

        // End resource container
        html += `</div>`;

        // Add spacing between questions
        if (index < resource.payload.questions.length - 1) {
            html += '\n\n';
        }
    }

    return addQuestionSetWrapper(html, resource, 'shortAnswer');
}

function convertMatchingToHtml(resource: ContentResource): string {
    let html = '';

    // Add instructions if present
    html += convertQuestionSetInstructionsToHtml(resource);

    const { options } = resource.payload;
    const terms = options?.terms || [];

    // Start matching container
    html += `<div data-caption="${escapeXml(resource.caption || '')}" data-type="matching" class="matching-container">`;

    // Add matching pairs
    terms.forEach((item: any) => {
        html += `<div data-type="matching-pair" class="matching-pair-item">`;

        // Add term
        html += `<div data-type="matching-pair-term">`;
        html += `<p>${item.term[0].text || ''}</p>`;
        html += `</div>`;

        // Add definition
        html += `<div data-type="matching-pair-definition">`;
        html += `<p>${item.definition[0].text || ''}</p>`;
        html += `</div>`;

        // Add explanation
        html += `<div data-type="matching-pair-explanation">`;
        html += `<p>${item.explanation[0].text || ''}</p>`;
        html += `</div>`;

        html += `</div>`;
    });

    html += `</div>`;

    return addQuestionSetWrapper(html, resource, 'matching');
}

export function convertMultipleChoiceToHtml(resource: ContentResource): string {
    let html = '';

    // Add instructions if present
    html += convertQuestionSetInstructionsToHtml(resource);

    // Convert each question
    resource.payload.questions.forEach((question: any, index: number) => {
        const questionText = extractHtml(question.text, resource);

        // Start building the HTML for this question
        html += `<div data-allow-multiple="false" data-title="Question ${index + 1}" data-type="multiple-choice" class="multiple-choice-container">`;

        // Add question container with updated data-type
        html += `<div data-type="mc-question">${questionText}</div>`;

        // Add choices
        question.choices.forEach((choice: any) => {
            const isLabelText = typeof choice.label === 'string';
            html += `<div data-correct="${choice.correct}" data-type="mc-choice">`;
            html += isLabelText ? choice.label : extractHtml(choice.label, resource);
            html += `<div data-type="mc-explanation">${extractHtml(choice.explanation, resource)}</div>`;
            html += `</div>`;
        });

        html += `</div>`;

        // Add spacing between questions
        if (index < resource.payload.questions.length - 1) {
            html += '\n\n';
        }
    });

    return addQuestionSetWrapper(html, resource, 'multipleChoice');
}

export function convertDetectAnswerToHtml(resource: ContentResource): string {
    let html = '';

    html += convertQuestionSetInstructionsToHtml(resource);

    resource.payload.questions.forEach((question: any, index: number) => {
        const contents = Array.isArray(question.contents) ? question.contents : [];
        const optionIdByButtonIndex = new Map<number, string>();

        let optionCount = 0;
        const questionParts = contents
            .map((contentItem: any, contentIndex: number) => {
                if (contentItem?.type !== 'button') {
                    return formatIndentedTextForHtml(contentItem?.text || '');
                }

                optionCount += 1;
                const optionId = `da-opt-${index + 1}-${optionCount}`;
                optionIdByButtonIndex.set(contentIndex, optionId);
                return `<span data-type="detect-answer-option-mark" data-option-id="${optionId}">${contentItem?.text || ''}</span>`;
            })
            .join('');

        html += `<div data-title="Question ${index + 1}" data-type="detect-answer" class="detect-answer-container">`;
        html += `<div data-type="detect-answer-question">${questionParts}</div>`;

        contents.forEach((contentItem: any, contentIndex: number) => {
            if (contentItem?.type !== 'button') {
                return;
            }

            const optionId = optionIdByButtonIndex.get(contentIndex);
            if (!optionId) {
                return;
            }

            const explanation = contentItem?.explanation || '';
            html += `<div data-type="detect-answer-option" data-option-id="${optionId}" data-option-text="${escapeXml(contentItem?.text || '')}" data-correct="${contentItem?.correct ? 'true' : 'false'}">`;
            html += `<div data-type="detect-answer-explanation">${explanation}</div>`;
            html += `</div>`;
        });

        html += `</div>`;

        if (index < resource.payload.questions.length - 1) {
            html += '\n\n';
        }
    });

    return addQuestionSetWrapper(html, resource, 'detectAnswer');
}

function addQuestionSetWrapper(html: string, resource: ContentResource, questionType: string, altText?: string): string {
    const nodeId = getNodeIdFromGuid(resource.guid);
    const escapedAltText = altText ? escapeXml(altText) : '';
    const altTextAttr = escapedAltText ? ` data-alt-text="${escapedAltText}"` : '';
    const questionSetAttr = resource.activity_type
        ? `class="question-set" data-id="${nodeId || ''}" data-activity-type="${resource.activity_type}" data-question-type="${questionType}" data-caption="${resource.caption || ''}"${altTextAttr}`
        : '';
    return `<div ${questionSetAttr}>${html}</div>`;
}

async function convertZyAnimatorToHtml(resource: ContentResource): Promise<string> {
    const { payload, guid, caption, width, height } = resource;
    const options = payload.options || {};
    const altText = payload.alt_text || '';
    const image_ids = payload.image_ids || [];

    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || options.caption || 'Animation';
    const animHeight = height ? `${height}px` : options.height || '350px';
    const animWidth = width ? `${width}px` : options.width || '600px';
    const animXMLToLoad = await getXmlFromResource(resource);
    if (animXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    const animatorVersion = animXMLToLoad.includes('made-with="2"') ? '2' : '1';

    return `<div data-animator-id="${nodeId}" data-caption="${escapeXml(captionText)}" data-anim-xml="${escapeXml(animXMLToLoad)}" data-height="${animHeight}" data-width="${animWidth}" data-type="zy-animator" data-animator-version="${animatorVersion}" data-alt-text="${escapeXml(altText)}" data-image-ids="${image_ids.join(',')}" class="zy-animator-container"></div>`;
}

function convertZyYoutubeVideoToHtml(resource: ContentResource): string {
    const { payload, guid, caption } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const link = payload.link || '';
    const captionText = caption || '';
    return `<div data-id="${nodeId}" data-caption="${escapeXml(captionText)}" data-link="${escapeXml(link)}" data-type="zy-youtube-video" class="zy-youtube-video-container"></div>`;
}

function convertZyClassicLabToHtml(resource: ContentResource): string {
    const { payload, guid } = resource;
    const markdown = payload.markdown;
    const instructionsText = typeof markdown === 'string' && markdown.trim() ? renderMarkdownToHtml(markdown) : '';
    const id = guid || '';
    return `<zyClassicLab id="${escapeXml(id)}"><zyClassicLabInstruction>${unescapeXml(instructionsText)}</zyClassicLabInstruction></zyClassicLab>`;
}

function convertZyITLabToHtml(resource: ContentResource): string {
    const { guid, caption, instructions, payload } = resource;
    return buildZyITLabHtml({
        guid: guid || '',
        caption: caption || '',
        instructions: typeof instructions === 'string' ? instructions : '',
        payload,
    });
}

function convertZyStudioToHtml(resource: ContentResource): string {
    const { guid, caption, instructions } = resource;
    const studioId = guid || '';
    const captionText = caption || '';
    // Attributed-string arrays must go through extractHtml; String(array) becomes "[object Object]".
    const instructionsText = Array.isArray(instructions)
        ? extractHtml(instructions, resource)
        : typeof instructions === 'string'
          ? instructions
          : '';

    return `<zyStudio id="${studioId}" guid="${studioId}" caption="${escapeXml(captionText)}"><zyStudioInstruction>${instructionsText}</zyStudioInstruction></zyStudio>`;
}

function convertIframeFromHtmlContent(item: ZyIframeHtmlContent): string {
    const src = item.src;
    const title = item.title ?? '';
    const width = item.width ?? '';
    const height = item.height ?? '';
    const sandbox = item.sandbox ?? '';
    const wileyHosted = item.wiley_hosted ? 'true' : '';

    let html = '<iframe';
    if (src) html += ` src="${escapeXml(src)}"`;
    if (width) html += ` width="${escapeXml(String(width))}"`;
    if (height) html += ` height="${escapeXml(String(height))}"`;
    if (title) html += ` title="${escapeXml(title)}"`;
    if (sandbox) html += ` sandbox="${escapeXml(sandbox)}"`;
    if (wileyHosted) html += ` wiley-hosted="${wileyHosted}"`;
    html += '></iframe>';

    return html;
}

function convertZyDEToHtml(resource: ContentResource): string {
    const { guid, payload } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const opts = payload?.options ?? {};
    const language = opts.language || 'cpp';
    const inputEnabled = Boolean(opts.input_enabled);
    const resetEnabled = Boolean(opts.reset_enabled);
    const stacked = Boolean(opts.stacked);
    const activityAttr = ` activity_type="${resource.activity_type === 'participation' ? 'participation' : 'tinker'}"`;

    let html = `<zyDE id="${escapeXml(nodeId || '')}" language="${escapeXml(String(language))}"`;
    const cap = resource.caption != null ? String(resource.caption).trim() : '';
    if (cap) {
        html += ` caption="${escapeXml(cap)}"`;
    }
    if (inputEnabled) {
        html += ' input="true"';
    }
    if (resetEnabled) {
        html += ' reset="true"';
    }
    if (stacked) {
        html += ' stacked="true"';
    }
    html += activityAttr;
    html += '>';

    // Add instructions if present
    if (resource.instructions && resource.instructions.length > 0) {
        html += '<zyDEInstruction>';
        html += extractHtml(resource.instructions, resource);
        html += '</zyDEInstruction>';
    }

    const stdin = opts.input;
    if (stdin != null && String(stdin) !== '') {
        html += `<zyInput>${escapeXml(String(stdin))}</zyInput>`;
    }

    const files: { program: string; filename?: string; main?: boolean; data?: boolean }[] =
        Array.isArray(opts.multifiles) && opts.multifiles.length > 0
            ? opts.multifiles
            : opts.program
              ? [{ program: opts.program, main: true }]
              : [];

    for (const f of files) {
        html += '<zyProgram';
        if (f.filename) {
            html += ` filename="${escapeXml(String(f.filename))}"`;
        }
        if (f.main) {
            html += ' main="true"';
        }
        if (f.data) {
            html += ' data="true"';
        }
        html += `>${escapeXml(String(f.program ?? ''))}</zyProgram>`;
    }

    const outputFiles = opts.outputFiles;
    if (outputFiles != null && String(outputFiles).trim() !== '') {
        html += `<zyOutputFiles>${escapeXml(String(outputFiles).trim())}</zyOutputFiles>`;
    }

    html += '</zyDE>';
    return html;
}

async function convertZyToolToHtml(resource: ContentResource): Promise<string> {
    const { activity_type, caption, guid, parts, payload } = resource;
    const { alt_text, image_ids, optional, options, tool, height, width } = payload;
    const nodeId = getNodeIdFromGuid(guid);
    const toolName = tool || '';
    const captionText = caption || '';
    const toolHeight = height ? `${height}px` : null;
    const toolWidth = width ? `${width}px` : null;
    const altText = alt_text || '';
    const toolInstructions = caption || '';
    const toolChallenge = activity_type === 'challenge' || false;
    const toolParts = parts || 1;
    const toolOptional = optional || false;
    const optionsData = options || {};
    const toolImageIds = image_ids || [];
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-tool-id="${nodeId}" data-tool-name="${escapeXml(toolName)}" data-tool-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-height="${toolHeight}" data-width="${toolWidth}" data-alt-text="${escapeXml(altText)}" data-instructions="${escapeXml(toolInstructions)}" data-challenge="${toolChallenge}" data-parts="${toolParts}" data-optional="${toolOptional}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-image-ids="${toolImageIds.join(',')}" data-type="zy-tool" class="zy-tool-container"></div>`;
}

function convertMathWorksLearningToolToHtml(resource: ContentResource): string {
    const guid = resource.guid || '';
    const captionText = resource.caption || '';
    const providerName = String(resource.payload?.provider_name || 'mathworks_learning_tool');
    const launchUrl = typeof resource.payload?.launch_url === 'string' ? resource.payload.launch_url : '';
    const launchData =
        resource.payload?.launch_data && typeof resource.payload.launch_data === 'object' ? resource.payload.launch_data : {};
    const resourceLinkId = typeof resource.payload?.resource_link_id === 'string' ? resource.payload.resource_link_id : '';
    const toolParts = resource.parts || 1;

    return `<div data-tool-id="${escapeXml(guid)}" data-provider-name="${escapeXml(providerName)}" data-lti-launch-url="${escapeXml(launchUrl)}" data-lti-launch-data="${escapeXml(JSON.stringify(launchData))}" data-lti-resource-link-id="${escapeXml(resourceLinkId)}" data-caption="${escapeXml(captionText)}" data-challenge="true" data-parts="${toolParts}" data-type="zy-mathworks-learning-tool" class="zy-mathworks-learning-tool-container"></div>`;
}

export function xmlFromContentResourcePayload(resource: ContentResource): string | null {
    const opts = resource.payload?.options;
    if (!opts || typeof opts !== 'object') {
        return null;
    }
    const tool = resource.payload?.tool;
    if (tool === 'zyAnimator' && typeof opts.animXMLToLoad === 'string' && opts.animXMLToLoad) {
        return opts.animXMLToLoad;
    }
    if (typeof opts.toolXMLToLoad === 'string' && opts.toolXMLToLoad) {
        return opts.toolXMLToLoad;
    }
    return null;
}

function missingZytagSnippetPlaceholderHtml(resource: ContentResource): string {
    const devBookName = resource.devBookName ?? resource.sectionMetadata?.devBookName ?? '';
    const chapter_number = String(resource.chapter_number ?? resource.sectionMetadata?.chapter_number ?? '');
    const snippetGuid = getNodeIdFromGuid(resource.guid ?? '') || String(resource.payload?.ref ?? '');
    const nodeId = snippetGuid || 'missing-zytag-snippet';
    const note = `MaZytagSnippets row not found: zybook_code=${devBookName}, guid=${snippetGuid}, chapter_number=${chapter_number}.`;
    return `<unsupported-editable id="${escapeXml(nodeId)}" data-missing-zytag-snippet="true" data-snippet-guid="${escapeXml(snippetGuid)}" data-snippet-chapter="${escapeXml(chapter_number)}" data-snippet-zybook="${escapeXml(devBookName)}">${escapeXml(note)}</unsupported-editable>`;
}

function onMissingZytagSnippet(resource: ContentResource): string {
    if (resource.sectionMetadata?.missingSnippetBehavior === 'placeholder') {
        return missingZytagSnippetPlaceholderHtml(resource);
    }
    const devBookName = resource.devBookName ?? resource.sectionMetadata?.devBookName ?? '';
    const chapter_number = String(resource.chapter_number ?? resource.sectionMetadata?.chapter_number ?? '');
    const snippetGuid = getNodeIdFromGuid(resource.guid ?? '') || String(resource.payload?.ref ?? '');
    throw new Error(`MaZytagSnippets row not found: zybook_code=${devBookName}, guid=${snippetGuid}, chapter_number=${chapter_number}.`);
}

async function getXmlFromResource(resource: ContentResource): Promise<string | null> {
    const fromPayload = xmlFromContentResourcePayload(resource);
    if (fromPayload) {
        return fromPayload;
    }
    const devBookName = resource.devBookName ?? resource.sectionMetadata?.devBookName;
    const chapter_number = resource.chapter_number ?? resource.sectionMetadata?.chapter_number;
    const GUIDToSearch = getNodeIdFromGuid(resource.guid);
    try {
        return await getXmlFromTag(devBookName || '', GUIDToSearch || '', String(chapter_number ?? ''), params =>
            zyAuthor.get('modern_authoring/zytag_snippets', { params }),
        );
    } catch (err) {
        if (isMissingZytagSnippetError(err)) {
            return null;
        }
        throw err;
    }
}

function containsZyCode(html: string): boolean {
    const codeClassRegex = /<div[^>]*\bclass="[^"]*\bcode\b[^"]*"[^>]*>/i;
    return codeClassRegex.test(html);
}

function containsLegacyZyMatrixHtml(html: string): boolean {
    return /<table[^>]*class=["'][^"']*\btable_matrix\b[^"']*["']/i.test(html);
}

function containsCodeBlock(html: string): boolean {
    const codeBlockRegex = /<code[^>]*>/i;
    return codeBlockRegex.test(html);
}

function normalizeMatrixText(value: string): string {
    return value
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function getDirectRowCells(row: HTMLTableRowElement): string[] {
    return Array.from(row.children)
        .filter((child): child is HTMLTableCellElement => child instanceof HTMLTableCellElement)
        .map(cell => normalizeMatrixText(cell.textContent || ''));
}

function getDirectRows(table: HTMLTableElement): HTMLTableRowElement[] {
    const rows: HTMLTableRowElement[] = [];

    Array.from(table.children).forEach(child => {
        if (child instanceof HTMLTableRowElement) {
            rows.push(child);
            return;
        }

        const tag = child.tagName.toLowerCase();
        if (tag === 'tbody' || tag === 'thead' || tag === 'tfoot') {
            rows.push(...Array.from(child.children).filter((row): row is HTMLTableRowElement => row instanceof HTMLTableRowElement));
        }
    });

    return rows;
}

function getDirectCells(row: HTMLTableRowElement): HTMLTableCellElement[] {
    return Array.from(row.children).filter((child): child is HTMLTableCellElement => child instanceof HTMLTableCellElement);
}

function getFirstDirectChildTable(element: Element): HTMLTableElement | null {
    return Array.from(element.children).find((child): child is HTMLTableElement => child instanceof HTMLTableElement) || null;
}

function extractMatrixRowsFromLegacyStructure(rootTable: HTMLTableElement): string[][] {
    const outerRow = getDirectRows(rootTable)[0];
    if (!outerRow) {
        return [];
    }

    // Legacy structure
    // table.table_matrix -> first row -> second cell -> border table -> first row -> second cell -> inner matrix table.
    const outerCells = getDirectCells(outerRow);
    const borderTable = outerCells[1] ? getFirstDirectChildTable(outerCells[1]) : null;
    if (!borderTable) {
        return [];
    }

    const borderRow = getDirectRows(borderTable)[0];
    if (!borderRow) {
        return [];
    }

    const borderCells = getDirectCells(borderRow);
    const matrixTable = borderCells[1] ? getFirstDirectChildTable(borderCells[1]) : null;
    if (!matrixTable) {
        return [];
    }

    return getDirectRows(matrixTable)
        .map(getDirectRowCells)
        .map(row => row.filter(cell => cell !== '' && cell !== '&nbsp;'))
        .filter(row => row.length > 1);
}

function getLegacyMatrixHtml(table: HTMLTableElement): string {
    const centeredWrapper = table.closest('center');
    if (centeredWrapper && centeredWrapper.querySelector('table.table_matrix') === table) {
        return centeredWrapper.outerHTML;
    }

    return table.outerHTML;
}

/**
 * Migrate code block format to zyCode block
 * @param html - HTML string containing code blocks
 * @returns HTML string with code blocks migrated to zyCode blocks
 */
export function migrateCodeBlockFormat(html: string): string {
    return html.replace(/<pre>\s*<code([^>]*)>([\s\S]*?)<\/code>\s*<\/pre>/gi, (_match, codeAttrs, content) => {
        const classMatch = /class=["']([^"']*)["']/i.exec(codeAttrs);
        const classAttr = classMatch?.[1] || '';
        const langMatch = classAttr.match(/(?:^|\s)language-([^\s]+)/);
        const language = langMatch?.[1] || detectLanguageFromCodeSnippet(content);
        return `<zyCode language="${escapeXml(language)}">${content}</zyCode>`;
    });
}

export function migrateLegacyZyMatrixFormat(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const legacyTables = Array.from(doc.querySelectorAll('table.table_matrix'));

    legacyTables.forEach(table => {
        if (!table.isConnected) {
            return;
        }

        const firstRow = table.querySelector('tr');
        const label = normalizeMatrixText(firstRow?.querySelector('td')?.textContent || '');
        const matrixSuper = normalizeMatrixText(table.querySelector('sup')?.textContent || '');
        const matrixRows = extractMatrixRowsFromLegacyStructure(table as HTMLTableElement);

        if (matrixRows.length === 0) {
            return;
        }

        const matrix = `[[${matrixRows.map(row => row.join(',')).join('],[')}]]`;
        const matrixNode = doc.createElement('zyMatrix');
        const legacyHtml = getLegacyMatrixHtml(table as HTMLTableElement);

        if (label) {
            matrixNode.setAttribute('label', label);
        }

        if (matrixSuper) {
            matrixNode.setAttribute('super', matrixSuper);
        }

        const centeredWrapper = table.closest('center');
        if (centeredWrapper) {
            matrixNode.setAttribute('center', 'true');
        }

        matrixNode.setAttribute('legacy-html', legacyHtml);

        matrixNode.textContent = matrix;

        if (centeredWrapper) {
            centeredWrapper.replaceWith(matrixNode);
            return;
        }

        table.replaceWith(matrixNode);
    });

    return doc.body.innerHTML;
}

export function containsZyVersion(html: string): boolean {
    return /<!-- BEGIN VERSION [^-]+ -->/.test(html);
}

export function containsZyCommonError(html: string): boolean {
    const codeClassRegex = /<span[^>]*\bclass="[^"]*\bcommon-error\b[^"]*"[^>]*>/i;
    return codeClassRegex.test(html);
}

export function containsZyGoodPractice(html: string): boolean {
    const codeClassRegex = /<span[^>]*\bclass="[^"]*\bgood-practice\b[^"]*"[^>]*>/i;
    return codeClassRegex.test(html);
}

export function containsZyProof(html: string): boolean {
    const proofTagRegex = /<zyProof[\s>]/i;
    const legacyProofRegex = /<span[^>]*>\s*<b>\s*Proof\.\s*<\/b>\s*<\/span>/i;
    return proofTagRegex.test(html) || legacyProofRegex.test(html);
}

export function containsZyReference(html: string): boolean {
    return /<zyReference[\s>]/i.test(html);
}

export function migrateZyReferenceFormat(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('zyreference').forEach(referenceNode => {
        // Preserve the zyReference tag with its attributes so it can be converted to a reference node.
        // The tag structure should be: <zyReference id="source_id" target_id="target_id">REFERENCE</zyReference>
        // This will be picked up by Tiptap's reference extension during ProseMirror JSON generation.
        const id = referenceNode.getAttribute('id');
        const targetId = referenceNode.getAttribute('target_id');

        if (id && targetId) {
            referenceNode.setAttribute('data-reference-source-id', id);
            referenceNode.setAttribute('data-reference-target-id', targetId);
            // Keep text content as 'REFERENCE' for visibility
            referenceNode.textContent = `REFERENCE`;
            // Mark as reference type for downstream processing
            referenceNode.setAttribute('data-type', 'reference');
        }
    });

    return doc.body.innerHTML;
}

export async function migrateZyProofFormat(html: string): Promise<string> {
    // Legacy input often looks like: <span style="display:block"><b>Proof.</b></span>...&#9632;
    // Insert an explicit line break after the "Proof." label so imported content starts on the next line.
    let migratedHtml = html.replace(
        /(<span[^>]*display\s*:\s*block[^>]*>\s*<b>\s*Proof\.\s*<\/b>\s*<\/span>)(?!\s*<br\s*\/?>)/gi,
        '$1<br/>',
    );

    // If proof text is already inside a zyProof tag and starts with "Proof.", enforce a newline after it.
    migratedHtml = migratedHtml.replace(/(<zyProof>\s*Proof\.\s*)(?!\n)/gi, '$1\n');

    return migratedHtml;
}

export async function migrateZyCommonErrorFormat(html: string): Promise<string> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('span.common-error').forEach(commonError => {
        const italic = doc.createElement('i');
        italic.textContent = commonError.textContent ?? '';
        commonError.replaceWith(italic);
    });

    return doc.body.innerHTML;
}

export async function migrateZyGoodPracticeFormat(html: string): Promise<string> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('span.good-practice').forEach(goodPractice => {
        const italic = doc.createElement('i');
        italic.textContent = goodPractice.textContent ?? '';
        goodPractice.replaceWith(italic);
    });

    return doc.body.innerHTML;
}

/**
 * Converts legacy `<!-- BEGIN VERSION ... -->...<!-- END VERSION ... -->` blocks
 * (produced by the backend parser when no content_version filter is set). This
 * only applies when zyVersion is not a top-level tag.
 *
 * The backend may prepend a version-label prefix (e.g. "cpp: ") to the content
 * inside the wrapping <div>. If present, it is stripped before wrapping.
 * Detection of block vs. inline is done on the raw inner content (after unwrapping
 * the outer <div>) before any prefix is removed:
 *
 * - innerContent contains block-level HTML elements → block format:
 *     <zyVersion version="cpp"><p>...</p></zyVersion>
 *
 * - innerContent is plain text or inline elements only → inline format:
 *     <span data-zy-version-inline version="cpp">text</span>
 */
export function migrateZyVersionFormat(html: string): string {
    const legacyVersionReplacements: Array<
        | {
              searchValue: string;
              replacementValue: string;
          }
        | {
              searchValue: RegExp;
              replacementValue: (match: string, text: string) => string;
          }
    > = [
        {
            searchValue:
                '<!-- BEGIN VERSION c, cpp --><div class="versionc, cpplang"><div style="background-color: #fffafa; border-color: #bbbbbb; border: 1px solid; display: inline-block; margin: 5px;">c, cpp:function</div></div><!-- END VERSION c, cpp -->',
            replacementValue: '<span data-zy-version-inline version="c,cpp">function</span>',
        },
        {
            searchValue:
                '<!-- BEGIN VERSION java --><div class="versionjavalang"><div style="background-color: #fffafa; border-color: #bbbbbb; border: 1px solid; display: inline-block; margin: 5px;">java:method</div></div><!-- END VERSION java -->',
            replacementValue: '<span data-zy-version-inline version="java,javaeo">method</span>',
        },
        {
            searchValue:
                /<!-- BEGIN VERSION c,\s*cpp -->\s*<div class=\\?"versionc,\s*cpplang\\?">\s*<div style=\\?"[^"]*\\?">\s*c,\s*cpp:\s*([\s\S]*?)\s*<\/div>\s*<\/div>\s*<!-- END VERSION c,\s*cpp -->/gi,
            replacementValue: (_match, text: string) => `<span data-zy-version-inline version="c,cpp">${text.trim()}</span>`,
        },
        {
            searchValue:
                /<!-- BEGIN VERSION java -->\s*<div class=\\?"versionjavalang\\?">\s*<div style=\\?"[^"]*\\?">\s*java:\s*([\s\S]*?)\s*<\/div>\s*<\/div>\s*<!-- END VERSION java -->/gi,
            replacementValue: (_match, text: string) => `<span data-zy-version-inline version="java">${text.trim()}</span>`,
        },
        {
            searchValue:
                '<!-- BEGIN VERSION c, cpp --><div class="versionc, cpplang"><div style="background-color: #fffafa; border-color: #bbbbbb; border: 1px solid; display: inline-block; margin: 5px;">c, cpp:data member</div></div><!-- END VERSION c, cpp -->',
            replacementValue: '<span data-zy-version-inline version="c,cpp">data member</span>',
        },
        {
            searchValue:
                '<!-- BEGIN VERSION java --><div class="versionjavalang"><div style="background-color: #fffafa; border-color: #bbbbbb; border: 1px solid; display: inline-block; margin: 5px;">java:field</div></div><!-- END VERSION java -->',
            replacementValue: '<span data-zy-version-inline version="java">field</span>',
        },
    ];

    for (const replacement of legacyVersionReplacements) {
        if (typeof replacement.searchValue === 'string' && typeof replacement.replacementValue === 'string') {
            html = html.replaceAll(replacement.searchValue, replacement.replacementValue);
        } else if (typeof replacement.replacementValue === 'function') {
            html = html.replace(replacement.searchValue, replacement.replacementValue);
        }
    }

    return html.replace(
        /<!-- BEGIN VERSION ([^-]+?) -->([\s\S]*?)<!-- END VERSION [^-]+? -->/g,
        (_match, version: string, content: string) => {
            const trimmedContent = content.trim();
            const innerContent = trimmedContent.replace(/^<div[^>]*>([\s\S]*)<\/div>$/i, '$1').trim();
            const normalizedVersion = version.trim();

            // Strip the version-label prefix the backend may prepend (e.g. "cpp: " or "python:").
            // Match against the exact version label to avoid stripping unrelated colons in content.
            const versionLabelRegex = new RegExp(`^${normalizedVersion}:\\s*`, 'i');
            const strippedContent = innerContent.replace(versionLabelRegex, '').trim();

            const hasBlockElements = /<(p|ul|ol|li|h[1-6]|blockquote|pre|div|table)[^>]*>/i.test(innerContent);
            if (hasBlockElements) {
                return `<zyVersion version="${normalizedVersion}">${strippedContent}</zyVersion>`;
            }
            return `<span data-zy-version-inline version="${normalizedVersion}">${strippedContent}</span>`;
        },
    );
}

export function migrateZyFunction(html: string): string {
    const cppFunctionReplacement = '<span data-zy-version-inline version="c,cpp">function</span>';
    const javaMethodReplacement = '<span data-zy-version-inline version="java,javaeo">method</span>';

    return html.replace(/ZYFUNCTION/gi, `${cppFunctionReplacement}${javaMethodReplacement}`);
}

function simpleConvertZyCode(html: string, resource: ContentResource, omitWrapper: boolean = false): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const preElement = doc.querySelector('pre');
    const idAttribute = resource.payload.ref ? `id="${resource.payload.ref}"` : '';
    if (!preElement) return '';
    return omitWrapper ? preElement.textContent || '' : `<zyCode ${idAttribute}>${preElement.textContent}</zyCode>`;
}

async function migrateZyCodeFormat(html: string, resource: ContentResource): Promise<string> {
    const rawXml = await getXmlFromResource(resource);
    if (rawXml === null) {
        return `${html}${onMissingZytagSnippet(resource)}`;
    }

    // Extract all <zycode> elements from the raw XML
    const zyCodeRegex = /<zycode[^>]*>[\s\S]*?<\/zycode>/gi;
    const zyCodeElements = rawXml.match(zyCodeRegex) || [];
    if (zyCodeElements.length === 0) {
        console.warn('No zyCode elements found in raw XML');
    }

    // Replace div elements with class "code" with corresponding zyCode elements
    const migratedHtml = html;
    let zyCodeIndex = 0;
    const zyCodeReplaceRegex = /<div[^>]*\bclass="[^"]*\bcode\b[^"]*"[^>]*><pre>[\s\S]*?<\/pre><\/div>/gi;
    const zyCodeReplaceRegexWithHighlight =
        /<div[^>]*\bclass="[^"]*\bcode\b[^"]*"[^>]*><div class="highlight"><pre>[\s\S]*?<\/pre><\/div><\/div>/gi;
    const escapeZyCodeContent = (zyCodeTag: string) => {
        return zyCodeTag.replace(
            /(<zycode[^>]*>)([\s\S]*?)(<\/zycode>)/i,
            (_m, open, content, close) => `${open}${escapeXml(normalizeZyCodeInnerText(content))}${close}`,
        );
    };

    const replaceZyCodeFunction = (match: string) => {
        if (zyCodeElements.length > zyCodeIndex) {
            const replacement = escapeZyCodeContent(zyCodeElements[zyCodeIndex]);
            zyCodeIndex++;
            return replacement;
        }
        return simpleConvertZyCode(match, resource);
    };
    return migratedHtml.replace(zyCodeReplaceRegex, replaceZyCodeFunction).replace(zyCodeReplaceRegexWithHighlight, replaceZyCodeFunction);
}

async function convertProgressionPlayerToHtml(resource: ContentResource): Promise<string> {
    const { activity_type, caption, guid, parts, payload } = resource;
    const { alt_text, image_ids, optional, options, tool } = payload;
    const nodeId = getNodeIdFromGuid(guid);
    const toolName = tool || '';
    const captionText = caption || '';
    const altText = alt_text || '';
    const toolInstructions = extractHtml(resource.instructions, resource) || '';
    const toolChallenge = activity_type === 'challenge' || false;
    const toolParts = parts || 1;
    const toolOptional = optional || false;
    const optionsData = options || {};
    const toolImageIds = image_ids || [];
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-progression-id="${nodeId}" data-tool-name="${escapeXml(toolName)}" data-progression-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-alt-text="${escapeXml(altText)}" data-instructions="${escapeXml(toolInstructions)}" data-challenge="${toolChallenge}" data-parts="${toolParts}" data-optional="${toolOptional}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-image-ids="${toolImageIds.join(',')}" data-type="progression-builder" class="progression-builder-container"></div>`;
}

async function convertBlockOrderingToHtml(resource: ContentResource): Promise<string> {
    const { activity_type, caption, guid, payload } = resource;
    const { options, tool } = payload;
    const nodeId = getNodeIdFromGuid(guid);
    const toolName = tool || '';
    const captionText = caption || '';
    const toolChallenge = activity_type === 'challenge' || false;
    const optionsData = options || {};
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-block-ordering-builder-id="${nodeId}" data-tool-name="${escapeXml(toolName)}" data-block-ordering-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-challenge="${toolChallenge}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-type="block-ordering-builder" class="block-ordering-builder-container"></div>`;
}

async function convertCodeWritingToHtml(resource: ContentResource): Promise<string> {
    const {
        activity_type,
        caption,
        guid,
        parts,
        payload: { options },
    } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || '';
    const toolChallenge = activity_type === 'challenge' || false;
    const toolParts = parts || 1;
    const optionsData = options || {};
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-code-writing-builder-id="${nodeId}" data-code-writing-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-challenge="${toolChallenge}" data-parts="${toolParts}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-type="code-writing-builder" class="codewriting-builder-container"></div>`;
}

async function convertCodeOutputToHtml(resource: ContentResource): Promise<string> {
    const {
        caption,
        guid,
        parts,
        payload: { options },
    } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || '';
    const toolParts = parts || 1;
    const optionsData = options || {};
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-code-output-builder-id="${nodeId}" data-code-output-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-parts="${toolParts}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-type="code-output-builder" class="codeoutput-builder-container"></div>`;
}

async function convertParsonsCodingToHtml(resource: ContentResource): Promise<string> {
    const {
        activity_type,
        caption,
        guid,
        payload: { options },
    } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || '';
    const toolChallenge = activity_type === 'challenge' || false;
    const optionsData = options || {};
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-parsons-coding-builder-id="${nodeId}" data-parsons-coding-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-challenge="${toolChallenge}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-type="parsons-coding-builder" class="parsons-coding-builder-container"></div>`;
}

async function convertOpenStaxPythonCodeRunnerBuilderToHtml(resource: ContentResource): Promise<string> {
    const {
        caption,
        guid,
        payload: { options },
    } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || '';
    const optionsData = options || {};
    const instructions = extractHtml(resource.instructions, resource);
    const toolXMLToLoad = await getXmlFromResource(resource);
    if (toolXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-open-stax-python-code-runner-builder-id="${nodeId}" data-open-stax-python-code-runner-xml="${escapeXml(toolXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-instructions="${escapeXml(instructions)}" data-type="open-stax-python-code-runner-builder" class="open-stax-python-coderunner-builder-container"></div>`;
}

async function convertStreamlitAppToHtml(resource: ContentResource): Promise<string> {
    const {
        caption,
        guid,
        payload: { options },
    } = resource;
    const nodeId = getNodeIdFromGuid(guid);
    const captionText = caption || '';
    const optionsData = options || {};
    const streamlitXMLToLoad = await getXmlFromResource(resource);
    if (streamlitXMLToLoad === null) {
        return onMissingZytagSnippet(resource);
    }
    return `<div data-streamlit-app-id="${nodeId}" data-streamlit-xml="${escapeXml(streamlitXMLToLoad)}" data-caption="${escapeXml(captionText)}" data-options="${escapeXml(JSON.stringify(optionsData))}" data-type="streamlit-app" class="streamlit-app-container"></div>`;
}
